import type {
  Workstation,
  Procedure,
  ProcedureWithSteps,
  Step,
  Part,
  Cycle,
  CycleFull,
  AlarmTarget,
  ProcedureDoc,
  WorkstationServiceStatus,
} from '@visionqc/shared';

/**
 * Typed client for the procguard authoring API. Mirrors the house `req` helper in
 * client.ts (JSON content-type only when there is a body; validation issues surfaced).
 */
async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const isJsonBody = init?.body !== undefined && !(init.body instanceof FormData);
  const res = await fetch(path, {
    ...init,
    headers: isJsonBody ? { 'content-type': 'application/json', ...init?.headers } : init?.headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    const issue = Array.isArray(body.issues) ? body.issues[0] : null;
    const detail = issue ? `${(issue.path ?? []).join('.') || 'field'}: ${issue.message}` : null;
    throw new Error(detail ? `${body.message}: ${detail}` : body.message || `Request failed: ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

const B = '/api/procguard';

export const procguardApi = {
  // Workstations
  listWorkstations: (customerId?: number) =>
    req<Workstation[]>(`${B}/workstations${customerId ? `?customerId=${customerId}` : ''}`),
  createWorkstation: (input: Record<string, unknown>) =>
    req<Workstation>(`${B}/workstations`, { method: 'POST', body: JSON.stringify(input) }),
  updateWorkstation: (id: number, patch: Record<string, unknown>) =>
    req<Workstation>(`${B}/workstations/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteWorkstation: (id: number) => req<{ ok: true }>(`${B}/workstations/${id}`, { method: 'DELETE' }),

  // Procedures
  listProcedures: (workstationId: number) => req<Procedure[]>(`${B}/procedures?workstationId=${workstationId}`),
  getProcedure: (id: number) => req<ProcedureWithSteps>(`${B}/procedures/${id}`),
  createProcedure: (input: Record<string, unknown>) =>
    req<Procedure>(`${B}/procedures`, { method: 'POST', body: JSON.stringify(input) }),
  updateProcedure: (id: number, patch: Record<string, unknown>) =>
    req<Procedure>(`${B}/procedures/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteProcedure: (id: number) => req<{ ok: true }>(`${B}/procedures/${id}`, { method: 'DELETE' }),
  replaceSteps: (id: number, steps: Step[]) =>
    req<ProcedureWithSteps>(`${B}/procedures/${id}/steps`, { method: 'PUT', body: JSON.stringify({ steps }) }),
  exportProcedure: (id: number) => req<ProcedureDoc>(`${B}/procedures/${id}/export`),
  importProcedure: (workstationId: number, procedure: ProcedureDoc) =>
    req<ProcedureWithSteps>(`${B}/procedures/import`, { method: 'POST', body: JSON.stringify({ workstationId, procedure }) }),

  // Parts master
  listParts: (customerId: number) => req<Part[]>(`${B}/parts?customerId=${customerId}`),
  createPart: (input: Record<string, unknown>) => req<Part>(`${B}/parts`, { method: 'POST', body: JSON.stringify(input) }),
  updatePart: (id: number, patch: Record<string, unknown>) =>
    req<Part>(`${B}/parts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deletePart: (id: number) => req<{ ok: true }>(`${B}/parts/${id}`, { method: 'DELETE' }),
  importParts: (customerId: number, parts: Array<{ partNumber: string; description: string; unit: string }>) =>
    req<{ imported: number }>(`${B}/parts/import`, { method: 'POST', body: JSON.stringify({ customerId, parts }) }),

  // Component models (trained identity)
  listModels: (customerId: number, workstationId?: number) =>
    req<ProcguardModel[]>(`${B}/models?customerId=${customerId}${workstationId ? `&workstationId=${workstationId}` : ''}`),
  uploadModel: (form: FormData) => req<ProcguardModel>(`${B}/models`, { method: 'POST', body: form }),
  activateModel: (id: number) => req<ProcguardModel>(`${B}/models/${id}/activate`, { method: 'POST' }),
  deactivateModel: (id: number) => req<{ ok: true }>(`${B}/models/${id}/deactivate`, { method: 'POST' }),
  deleteModel: (id: number) => req<{ ok: true }>(`${B}/models/${id}`, { method: 'DELETE' }),

  // Alarm config
  getAlarm: (workstationId: number) => req<{ targets: AlarmTarget[] }>(`${B}/workstations/${workstationId}/alarm`),
  setAlarm: (workstationId: number, targets: AlarmTarget[]) =>
    req<{ targets: AlarmTarget[] }>(`${B}/workstations/${workstationId}/alarm`, { method: 'PUT', body: JSON.stringify({ targets }) }),

  // Operator kiosk — one poll for the whole workstation state.
  getLive: (workstationId: number) =>
    req<{ workstation: Workstation; procedure: ProcedureWithSteps | null; cycle: CycleFull | null }>(
      `${B}/workstations/${workstationId}/live`,
    ),
  override: (cycleId: number, seq: number, reason: string) =>
    req<{ ok: true }>(`${B}/cycles/${cycleId}/override`, { method: 'POST', body: JSON.stringify({ seq, reason }) }),
  reset: (workstationId: number) => req<{ ok: true }>(`${B}/workstations/${workstationId}/reset`, { method: 'POST' }),
  getStatus: (workstationId: number) =>
    req<WorkstationServiceStatus>(`${B}/workstations/${workstationId}/status`),
  assess: (cycleId: number, operator: string, assessment: Record<string, unknown>) =>
    req<{ ok: true }>(`${B}/cycles/${cycleId}/assess`, { method: 'POST', body: JSON.stringify({ operator, assessment }) }),

  // Cycles (read)
  listCycles: (params: { workstationId?: number; customerId?: number; limit?: number }) => {
    const q = new URLSearchParams();
    if (params.workstationId) q.set('workstationId', String(params.workstationId));
    if (params.customerId) q.set('customerId', String(params.customerId));
    if (params.limit) q.set('limit', String(params.limit));
    return req<Cycle[]>(`${B}/cycles?${q.toString()}`);
  },
  getCycle: (id: number) => req<CycleFull>(`${B}/cycles/${id}`),
  reportUrl: (cycleId: number) => `${B}/cycles/${cycleId}/report`,
  getDetections: (workstationId: number) =>
    req<{ width: number; height: number; detections: Array<{ cls: string; x: number; y: number; w: number; h: number; confidence: number }> }>(
      `${B}/workstations/${workstationId}/detect`,
    ),

  analytics: (params: { workstationId?: number; customerId?: number; window?: string }) => {
    const q = new URLSearchParams();
    if (params.workstationId) q.set('workstationId', String(params.workstationId));
    if (params.customerId) q.set('customerId', String(params.customerId));
    if (params.window) q.set('window', params.window);
    return req<ProcguardAnalytics>(`${B}/analytics?${q.toString()}`);
  },
};

export interface ProcguardModel {
  id: number;
  customerId: number;
  workstationId: number | null;
  name: string;
  objectKey: string;
  classes: string[];
  inputSize: number;
  active: boolean;
  createdAt: string;
}

export interface ProcguardAnalytics {
  window: string;
  totals: { cycles: number; ok: number; nok: number; aborted: number };
  compliancePct: number;
  firstTimeRightPct: number;
  deviationRate: number;
  avgDurationMs: number | null;
  timeseries: Array<{ t: string; ok: number; nok: number; aborted: number }>;
  topDeviations: Array<{ kind: string; count: number }>;
  perStep: Array<{ seq: number; name: string; deviations: number }>;
  perOperator: Array<{ operator: string; cycles: number; firstTimeRight: number; avgDurationMs: number | null }>;
  kitting: { kitsVerified: number; kitsWithDiscrepancy: number; discrepancyPct: number };
}

/** Safety cameras (admin) — reused as the procguard workstation camera pool. */
export interface SafetyCameraLite {
  id: number;
  name: string;
  source: string;
  customerId: number | null;
  customerName: string | null;
}
export const listSafetyCameras = () => req<SafetyCameraLite[]>('/api/safety/cameras');

/** Create a safety camera already assigned to a customer (dedicated). */
export const createSafetyCamera = (input: { name: string; customerId: number; source?: string; location?: string }) =>
  req<{ id: number }>('/api/safety/cameras', { method: 'POST', body: JSON.stringify({ source: 'upload', ...input }) });
